import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type Rule } from '../services/types';

interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage?: string; // Optional message from the server
  data?: Rule; // The actual data returned by the API
  error?: string | null; // Error details in case of failure
  status?: number;
  ok?: boolean;
}

/**
 * Sends a request to update a rule using the external API service.
 *
 * @async
 * @function applyRule
 * @param {Rule} rule - The rule object containing the updated data.
 * @returns {Promise<Rule>} A promise that resolves to the updated rule data.
 */
const applyRule = async (str: string) => {
  const response = (await ExternalService.postApplyRules(str)) as ApiResponse;

  return response;
};

/**
 * Custom React hook to apply rules using React Query's `useMutation`.
 *
 * @function useApplyRules
 * @returns {import('@tanstack/react-query').UseMutationResult<Rule, unknown, Rule, unknown>} A mutation object that includes the mutate function, mutation status, and more.
 *
 */
const useApplyRules = () =>
  useMutation({
    mutationFn: (str: string) => applyRule(str), // Function to fetch data
  });

export default useApplyRules;
